import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  SearchBox,
} from '@octano/global-ui';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { usePerformanceAnalysisFiltersLoader } from './PerformanceAnalysisFiltersLoader';
import { usePerformanceAnalysisList } from './PerformanceAnalysisListLoader';

interface SearchFiltersType {
  studyPlanVersion: OutlinedSelectOptionType | null;
  course: OutlinedSelectOptionType | null;
  section: OutlinedSelectOptionType | null;
  teacherNameOrId: string;
  studentNameOrId: string;
}

export function PerformanceAnalysisFilters() {
  const data = usePerformanceAnalysisFiltersLoader();
  const { updateQuery } = usePerformanceAnalysisList();
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_PERFORMANCE_ANALYSIS,
  );
  const { control, handleSubmit, reset, setValue } = useForm<SearchFiltersType>(
    {
      defaultValues: {
        studyPlanVersion: null,
        course: null,
        section: null,
        teacherNameOrId: '',
        studentNameOrId: '',
      },
    },
  );
  const localSubmit = useCallback(
    (values: SearchFiltersType) => {
      updateQuery((query) => ({
        page: 1,
        periodId: query.periodId,
        studyPlanVersionId:
          (values.studyPlanVersion?.value as number) ?? undefined,
        courseId: (values.course?.value as number) ?? undefined,
        sectionId: (values.section?.value as number) ?? undefined,
        teacherNameOrId: values.teacherNameOrId,
        studentNameOrId: values.studentNameOrId,
      }));
    },
    [updateQuery],
  );

  return (
    <form onSubmit={handleSubmit(localSubmit)}>
      <Row>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="studyPlanVersion"
            label={t('filters.studyPlanVersion')}
            placeholder={t('filters.studyPlanVersionPlaceholder')}
            options={data.studyPlanVersions.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>{' '}
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="course"
            label={t('filters.course')}
            placeholder={t('filters.coursePlaceholder')}
            options={data.courses.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="section"
            label={t('filters.section')}
            placeholder={t('filters.sectionPlaceholder')}
            options={data.sections.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        <Col xs={12} md={4}>
          <SearchBox
            name="teacherNameOrId"
            label={t('filters.teacher')}
            placeholder={t('filters.teacherPlaceholder')}
            control={control}
            clear={() => setValue('teacherNameOrId', '')}
          />
        </Col>
        <Col xs={12} md={4}>
          <SearchBox
            name="studentNameOrId"
            label={t('filters.student')}
            placeholder={t('filters.studentPlaceholder')}
            control={control}
            clear={() => setValue('studentNameOrId', '')}
          />
        </Col>
        <Col xs={12} md={4} className="ml-auto">
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              color="primary"
              className="mb-3"
              size="md"
              text={t('filters.search')}
            />
            <Button
              type="submit"
              color="primary"
              outlined
              className="mb-3 ml-2"
              onClick={() => {
                reset();
              }}
              size="md"
              text={t('filters.clean')}
            />
          </div>
        </Col>
      </Row>
    </form>
  );
}
